export type StickerSortBy = "name" | "date";

export type StickerSearchItem = {
  product_id?: string | number;
  product_name?: string;
  product_image?: string;
  createdon?: string;
  _searchName?: string;
};

export const SEARCH_ALL_TABS_MIN_CHARS = 2;
export const DEFAULT_STICKER_GRID_WIDTH = 400;
export const DEFAULT_STICKER_GRID_HEIGHT = 420;
export const STICKER_CELL_MIN = 72;
export const STICKER_GRID_GAP = 8;
export const STICKER_GRID_OVERSCAN_ROWS = 3;
export const STICKER_KEYSTROKE_BUDGET_MS = 50;
export const STICKER_MAX_VISIBLE_BUTTONS = 80;

export const getStickerName = (sticker: StickerSearchItem): string =>
  String(sticker?.product_name || "").trim();

export const normalizeStickerSortBy = (value: string | null): StickerSortBy =>
  value === "date" ? "date" : "name";

export const createdOnValue = (value?: string) => {
  const parsed = Date.parse(String(value || "").replace(" ", "T"));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const compareByName = (a: StickerSearchItem, b: StickerSearchItem) => {
  const aName = getStickerName(a);
  const bName = getStickerName(b);
  if (!aName && !bName) return 0;
  if (!aName) return 1;
  if (!bName) return -1;
  return aName.localeCompare(bName);
};

const compareByDate = (a: StickerSearchItem, b: StickerSearchItem) =>
  createdOnValue(b.createdon) - createdOnValue(a.createdon);

export const indexStickerSearchNames = <T extends StickerSearchItem>(
  items: T[]
): Array<T & { _searchName: string }> =>
  items.map((item) => ({
    ...item,
    _searchName: getStickerName(item).toLowerCase(),
  }));

export const sortStickerCollectionOnce = <T extends StickerSearchItem>(
  items: T[],
  sortedBy: StickerSortBy
): Array<T & { _searchName: string }> => {
  const indexed = indexStickerSearchNames(items);
  switch (sortedBy) {
    case "name":
      return indexed.sort(compareByName);
    case "date":
      return indexed.sort(compareByDate);
    default: {
      const _exhaustive: never = sortedBy;
      return _exhaustive;
    }
  }
};

export const filterSortedStickers = <T extends StickerSearchItem>(
  sortedItems: T[],
  filter: string
): T[] => {
  const query = String(filter || "").trim().toLowerCase();
  if (!query) {
    return sortedItems;
  }
  return sortedItems.filter((sticker) =>
    String(sticker._searchName || getStickerName(sticker).toLowerCase()).includes(
      query
    )
  );
};

export const getStickerSearchSource = <T,>(
  tabItems: T[],
  allItems: T[],
  filter: string
): T[] => {
  const query = String(filter || "").trim();
  if (query.length >= SEARCH_ALL_TABS_MIN_CHARS) {
    return allItems;
  }
  return tabItems;
};

export const readCssPixelVar = (
  element: { ownerDocument?: Document } | HTMLElement | null,
  name: string,
  fallback: number
) => {
  if (!element || typeof window === "undefined" || typeof getComputedStyle !== "function") {
    return fallback;
  }
  const parsed = parseFloat(getComputedStyle(element as Element).getPropertyValue(name));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const getStickerGridLayout = (
  width: number,
  minCell = STICKER_CELL_MIN,
  gap = STICKER_GRID_GAP
) => {
  const safeWidth = Math.max(minCell, width || DEFAULT_STICKER_GRID_WIDTH);
  const columns = Math.max(
    1,
    Math.floor((safeWidth + gap) / (minCell + gap))
  );
  const cellSize = Math.max(
    minCell,
    Math.floor((safeWidth - gap * (columns - 1)) / columns)
  );
  return {
    columns,
    cellSize,
    gap,
    rowHeight: cellSize + gap,
  };
};

export const getVisibleStickerRange = (
  itemCount: number,
  columns: number,
  rowHeight: number,
  scrollTop: number,
  viewportHeight: number,
  overscan = STICKER_GRID_OVERSCAN_ROWS
) => {
  const safeColumns = Math.max(1, columns);
  const safeRowHeight = Math.max(rowHeight, 1);
  const rowCount = itemCount === 0 ? 0 : Math.ceil(itemCount / safeColumns);
  const visibleRows =
    Math.ceil(Math.max(viewportHeight, 1) / safeRowHeight) + overscan * 2;
  const unclampedStartRow = Math.max(
    0,
    Math.floor(Math.max(0, scrollTop) / safeRowHeight) - overscan
  );
  const maxStartRow = Math.max(0, rowCount - visibleRows);
  const startRow = Math.min(unclampedStartRow, maxStartRow);
  const endRow = Math.min(rowCount, startRow + visibleRows);
  return {
    startRow,
    endRow,
    startIndex: startRow * safeColumns,
    endIndex: Math.min(itemCount, endRow * safeColumns),
    rowCount,
    totalHeight: rowCount * safeRowHeight,
  };
};

export const getStickerItemKey = (
  item: StickerSearchItem,
  fallbackIndex: number
) => {
  const productId = String(item?.product_id ?? "").trim();
  if (productId) {
    return productId;
  }
  const image = String(item?.product_image ?? "").trim();
  if (image) {
    return image;
  }
  return `sticker-${fallbackIndex}`;
};

export const getStableStickerItemKeys = (items: StickerSearchItem[]) => {
  const seen = new Map<string, number>();
  return items.map((item, index) => {
    const base = getStickerItemKey(item, index);
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}#${count}`;
  });
};

export const omitStickerSearchIndex = <T extends StickerSearchItem>(
  item: T
): Omit<T, "_searchName"> => {
  if (!item || item._searchName === undefined) {
    return item;
  }
  const rest = { ...item };
  delete rest._searchName;
  return rest;
};
